import { mirrorOnRootSpan } from '@lib/observability/utils/mirror-on-root-span';
import * as Sentry from '@sentry/nextjs';
import 'client-only';

import type { DealPackageNavigationTimings } from './collect-deal-package-navigation-timings.client';
import {
  DEAL_PACKAGE_ROUTE_ATTR,
  DEAL_PACKAGE_ROUTE_NAV_METRIC_NAMES,
  type DealPackageRouteCallerKind,
} from './constants';

interface EmitDealPackageNavigationMetricsInput {
  timings: DealPackageNavigationTimings;
  hasSubmissionId: boolean;
  callerKind?: DealPackageRouteCallerKind;
}

/**
 * Publishes Navigation Timing slices as Sentry distribution metrics (unsampled)
 * and mirrors key values onto the active pageload root span when sampled.
 */
export function emitDealPackageNavigationMetrics({
  timings,
  hasSubmissionId,
  callerKind = 'unknown',
}: EmitDealPackageNavigationMetricsInput): void {
  try {
    const attributes = {
      has_submission_id: hasSubmissionId,
      caller_kind: callerKind,
    };

    mirrorOnRootSpan({
      [DEAL_PACKAGE_ROUTE_ATTR.HAS_SUBMISSION_ID]: hasSubmissionId,
      [DEAL_PACKAGE_ROUTE_ATTR.CALLER_KIND]: callerKind,
    });

    const active = Sentry.getActiveSpan();
    if (active) {
      const root = Sentry.getRootSpan(active);
      root.setAttribute('deal_package.nav.ttfb_ms', timings.ttfb_ms);
      root.setAttribute('deal_package.nav.download_ms', timings.download_ms);
      root.setAttribute(
        'deal_package.nav.load_event_ms',
        timings.load_event_ms
      );
    }

    for (const [key, value] of Object.entries(timings)) {
      const metricName =
        DEAL_PACKAGE_ROUTE_NAV_METRIC_NAMES[
          key as keyof typeof DEAL_PACKAGE_ROUTE_NAV_METRIC_NAMES
        ];
      if (!metricName || value < 0) continue;
      Sentry.metrics.distribution(metricName, value, {
        unit: 'millisecond',
        attributes,
      });
    }
  } catch {
    // Observability must never interrupt page initialization.
  }
}
